/**
 * TimelineAxis Component
 *
 * Renders date tick marks below the timeline.
 * Automatically calculates appropriate tick intervals based on visible range.
 * Responds to zoom/pan transform.
 */

import { useMemo } from 'react'
import dayjs from 'dayjs'
import type { TimelineTick, TimelineTransform } from '../types'

interface TimelineAxisProps {
  /** Minimum date in milliseconds */
  minDate: number
  /** Maximum date in milliseconds */
  maxDate: number
  /** Container width in pixels */
  containerWidth: number
  /** Current zoom/pan transform */
  transform: TimelineTransform
  /** Height of the axis. Default: 24 */
  height?: number
}

/**
 * Generate tick marks for the timeline axis based on visible range
 */
function generateTicks(
  minDate: number,
  maxDate: number,
  containerWidth: number,
  transform: TimelineTransform
): TimelineTick[] {
  const ticks: TimelineTick[] = []

  // Calculate the visible date range based on transform
  const totalSpan = maxDate - minDate

  // When zoomed in, we see a smaller portion of the timeline
  // Reverse the transform to find what dates are visible
  const visibleStartMs = minDate + (-transform.x / transform.k) / containerWidth * totalSpan
  const visibleEndMs = visibleStartMs + totalSpan / transform.k

  const visibleSpan = visibleEndMs - visibleStartMs
  const spanDays = visibleSpan / (24 * 60 * 60 * 1000)

  // Determine tick interval based on visible span
  let tickInterval: 'day' | 'week' | 'month' | 'quarter' | 'year'
  let monthIncrement: number
  let showYearOnAllMonths = false  // Whether to show year with month labels

  if (spanDays <= 14) {
    tickInterval = 'day'
    monthIncrement = 0
  } else if (spanDays <= 60) {
    tickInterval = 'week'
    monthIncrement = 0
  } else if (spanDays <= 180) {
    tickInterval = 'month'
    monthIncrement = 1
  } else if (spanDays <= 365) {
    tickInterval = 'month'
    monthIncrement = 2
  } else if (spanDays <= 730) {
    // 1-2 years: show quarters with year
    tickInterval = 'quarter'
    monthIncrement = 3
    showYearOnAllMonths = true
  } else if (spanDays <= 1095) {
    // 2-3 years: show quarters with year
    tickInterval = 'quarter'
    monthIncrement = 6
    showYearOnAllMonths = true
  } else {
    // 3+ years: show only years
    tickInterval = 'year'
    monthIncrement = 12
  }

  // Generate ticks based on interval
  // IMPORTANT: Ticks must be anchored to fixed calendar positions (not relative to viewport)
  // This prevents "jumping" as user pans - ticks slide smoothly with content
  const startDate = new Date(visibleStartMs)
  let tickDate: Date

  if (tickInterval === 'day') {
    // Anchor to calendar days - start from beginning of the day containing visibleStartMs
    tickDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
    while (tickDate.getTime() < visibleEndMs) {
      const tickMs = tickDate.getTime()
      // Calculate position with transform applied
      const basePosition = ((tickMs - minDate) / totalSpan) * containerWidth
      const position = (basePosition * transform.k + transform.x) / containerWidth * 100

      if (position > 2 && position < 98) {
        ticks.push({
          position,
          label: dayjs(tickDate).format('D'),
          isYear: false,
        })
      }
      tickDate.setDate(tickDate.getDate() + 1)
    }
  } else if (tickInterval === 'week') {
    // Anchor to calendar weeks - find the Sunday at or before visibleStartMs
    tickDate = new Date(startDate)
    tickDate.setDate(tickDate.getDate() - tickDate.getDay()) // Go back to Sunday
    tickDate.setHours(0, 0, 0, 0)
    while (tickDate.getTime() < visibleEndMs) {
      const tickMs = tickDate.getTime()
      const basePosition = ((tickMs - minDate) / totalSpan) * containerWidth
      const position = (basePosition * transform.k + transform.x) / containerWidth * 100

      if (position > 2 && position < 98) {
        ticks.push({
          position,
          label: dayjs(tickDate).format('MMM D'),
          isYear: false,
        })
      }
      tickDate.setDate(tickDate.getDate() + 7)
    }
  } else {
    // Month/Quarter/Year - anchor to consistent calendar positions
    // Always start from January and use fixed increments to prevent odd/even alternation
    const year = startDate.getFullYear()
    const currentMonth = startDate.getMonth()

    // Find the first tick month at or before current month that aligns with increment
    // e.g., for bi-monthly (increment=2): Jan=0, Mar=2, May=4...
    // If we're in April (month 3), nearest aligned month before is Mar (month 2)
    const increment = monthIncrement || 1
    const alignedMonth = Math.floor(currentMonth / increment) * increment
    tickDate = new Date(year, alignedMonth, 1)

    while (tickDate.getTime() < visibleEndMs) {
      const tickMs = tickDate.getTime()
      const basePosition = ((tickMs - minDate) / totalSpan) * containerWidth
      const position = (basePosition * transform.k + transform.x) / containerWidth * 100

      if (position > 2 && position < 98) {
        // Format labels based on interval and span
        const isJanuary = tickDate.getMonth() === 0
        const month = tickDate.getMonth()
        const quarter = Math.floor(month / 3) + 1
        const yearShort = dayjs(tickDate).format("'YY")
        let label: string
        let isYear = false

        if (tickInterval === 'year') {
          // Year interval: just show year
          label = dayjs(tickDate).format('YYYY')
          isYear = true
        } else if (tickInterval === 'quarter' && showYearOnAllMonths) {
          // Multi-year span with quarters: show "Q1 '25" format
          label = isJanuary
            ? dayjs(tickDate).format('YYYY')
            : `Q${quarter} ${yearShort}`
          isYear = isJanuary
        } else if (showYearOnAllMonths) {
          // Multi-year span with months: show "MMM 'YY" format
          label = isJanuary
            ? dayjs(tickDate).format('YYYY')
            : dayjs(tickDate).format("MMM 'YY")
          isYear = isJanuary
        } else {
          // Single year span: show year only on January
          label = isJanuary
            ? dayjs(tickDate).format('YYYY')
            : dayjs(tickDate).format('MMM')
          isYear = isJanuary
        }

        ticks.push({
          position,
          label,
          isYear,
        })
      }
      tickDate.setMonth(tickDate.getMonth() + (monthIncrement || 1))
    }
  }

  return ticks
}

/**
 * TimelineAxis Component
 */
export function TimelineAxis({
  minDate,
  maxDate,
  containerWidth,
  transform,
  height = 24,
}: TimelineAxisProps) {
  // Generate ticks based on visible range and transform
  const ticks = useMemo(() => {
    return generateTicks(minDate, maxDate, containerWidth, transform)
  }, [minDate, maxDate, containerWidth, transform])

  return (
    <div className="timeline-axis" style={{ height }}>
      {ticks.map((tick, index) => (
        <div
          key={`${tick.position}-${index}`}
          className={`timeline-axis__tick ${tick.isYear ? 'timeline-axis__tick--year' : ''}`}
          style={{ left: `${tick.position}%` }}
        >
          <div className="timeline-axis__tick-line" />
          <span className="timeline-axis__tick-label">{tick.label}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Standalone TimelineAxis that doesn't require transform
 * (for use in simple single-timeline scenarios without zoom)
 */
export function TimelineAxisStandalone({
  minDate,
  maxDate,
  containerWidth,
  height = 24,
}: Omit<TimelineAxisProps, 'transform'>) {
  // Use identity transform (no zoom/pan)
  const transform: TimelineTransform = { x: 0, y: 0, k: 1 }

  return (
    <TimelineAxis
      minDate={minDate}
      maxDate={maxDate}
      containerWidth={containerWidth}
      transform={transform}
      height={height}
    />
  )
}

export default TimelineAxis
